"use client";

import { useSyncExternalStore } from "react";
import { Tabs } from "@mantine/core";

import {
  CONNECT_CLIENT,
  CONNECT_CLIENT_OPTIONS,
  type ConnectClient,
} from "@/app/(marketing)/integrations/constants";

import {
  parseConnectClient,
  readConnectHash,
  readServerConnectHash,
  subscribeToConnectHash,
  writeConnectHash,
} from "./connect-hash";

interface ConnectTabsProps {
  /** One walkthrough per client; every panel ships in the server HTML */
  panels: Record<ConnectClient, React.ReactNode>;
}

/** The Connect chapter's per-client tabs, driven by the URL hash: a #claude-code
    link selects that tab, the hero's chooser writes the hash to switch tabs, and
    clicking a tab writes it back, so the address bar always names the tab on
    screen. Inactive panels stay in the markup as display:none so a crawler or a
    no-JS visitor still gets every walkthrough. */
export default function ConnectTabs({ panels }: ConnectTabsProps) {
  const hashClient = useSyncExternalStore(
    subscribeToConnectHash,
    readConnectHash,
    readServerConnectHash,
  );
  const active = hashClient ?? CONNECT_CLIENT.CLAUDE;

  const handleChange = (value: string | null) => {
    const next = parseConnectClient(value ?? "");

    if (next) {
      writeConnectHash(next);
    }
  };

  return (
    <Tabs value={active} onChange={handleChange} color="blue" keepMountedMode="display-none">
      <Tabs.List mb="lg">
        {CONNECT_CLIENT_OPTIONS.map((option) => (
          <Tabs.Tab key={option.value} value={option.value}>
            {option.label}
          </Tabs.Tab>
        ))}
      </Tabs.List>

      {CONNECT_CLIENT_OPTIONS.map((option) => (
        <Tabs.Panel key={option.value} value={option.value}>
          {panels[option.value]}
        </Tabs.Panel>
      ))}
    </Tabs>
  );
}
